import * as React from "react";

import { AddressByMapbox, AddressComponent } from "@/components/AddressComponent";
import { ArrowIcon, EditIcon } from "@/components/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { UrbanFeaturePointFormValues, urbanFeaturePointSchema } from "./urban-feature-form.schema";

import { Button } from "@/components/ui/button";
import { ICoordinates } from "@/types/address.type";
import { Input } from "@/components/ui/input";
import { UrbanFeature } from "@/modules/urban-feature/urban-feature.types";
import { UrbanFeatureModalState } from "@/modules/urban-feature/urban-feature.const";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

interface UrbanFeaturePromptProps {
  coordinates?: ICoordinates;
  urbanFeature: UrbanFeature;
  onSave: (input: UrbanFeature) => Promise<void>;
  urbanFeatureModalState: keyof typeof UrbanFeatureModalState;
  setUrbanFeatureModalState: React.Dispatch<React.SetStateAction<keyof typeof UrbanFeatureModalState>>;
  onClose: () => void;
  setUrbanFeatureDetail: (value: UrbanFeature) => void;
}

export const TrafficLightModal = ({
  coordinates,
  urbanFeature,
  urbanFeatureModalState,
  setUrbanFeatureModalState,
  onClose,
  onSave,
  setUrbanFeatureDetail,
}: UrbanFeaturePromptProps) => {
  const form = useForm<UrbanFeaturePointFormValues>({
    resolver: zodResolver(urbanFeaturePointSchema),
    defaultValues: {
      ...urbanFeature,
      name: urbanFeature.name ?? undefined,
    },
    mode: "all",
  });

  const {
    formState: { isSubmitting },
    control,
  } = form;

  const handleSelectPlace = ({ geometry }: AddressByMapbox) => {
    setUrbanFeatureDetail({
      ...urbanFeature,
      geometry: geometry ?? urbanFeature.geometry,
    });
  };

  const handleClose = () => {
    onClose();
  };

  const handleMinimizeModal = () => {
    setUrbanFeatureModalState(UrbanFeatureModalState.MINIMIZE);
  };

  const handleShowModal = () => {
    setUrbanFeatureModalState(UrbanFeatureModalState.SHOW);
  };

  const onSubmit = async (input: UrbanFeaturePointFormValues) => {
    const formattedInput = {
      ...urbanFeature,
      ...input,
    };
    await onSave({ ...formattedInput, cityId: urbanFeature.cityId });
  };
  const modalMinimize = urbanFeatureModalState === UrbanFeatureModalState.MINIMIZE;

  return (
    <>
      <Dialog open={!modalMinimize}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{`${urbanFeature.id ? "Edit " : "Add "}a Traffic light`}</DialogTitle>
            <DialogDescription>Traffic light information</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold">Location:</span>
                {urbanFeature?.geometry && (
                  <Button className="text-blue-500" onClick={handleMinimizeModal} variant="ghost" type="button">
                    {urbanFeature?.geometry.coordinates.toString()}
                  </Button>
                )}
                <EditIcon className="w-5 h-5 cursor-pointer fill-black" onClick={handleMinimizeModal} />
              </div>
              <AddressComponent onSelectAddress={handleSelectPlace} coordinates={coordinates} />
              <DialogFooter className="sm:justify-between">
                <Button onClick={handleClose} variant="secondary" type="button">
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="submit">
                  Save
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <Button
        className={clsx("absolute bottom-12 right-4 shadow-md animate-bounce", modalMinimize ? "flex" : "hidden")}
        onClick={handleShowModal}
        variant="outline"
      >
        <span>Back to edit urban feature</span>
        <ArrowIcon className="w-3 h-3 rotate-90" />
      </Button>
    </>
  );
};
